import { useState, useEffect } from "react";
import { THEME_CACHE_KEYS, CACHE_OPTIONS } from "./constants";

// Use localStorage for persistent storage
const getStorageItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.error('Error accessing localStorage:', e);
    return null;
  }
};

const setStorageItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.error('Error writing to localStorage:', e);
    return false;
  }
};

const darkModeOptions = Object.entries(CACHE_OPTIONS[THEME_CACHE_KEYS.darkMode]);

const cachedOption = getStorageItem(THEME_CACHE_KEYS.darkMode);
const isValidOption = darkModeOptions.find(([key]) => key === cachedOption);
const defaultSetting = isValidOption ? cachedOption : THEME_CACHE_KEYS.system;

function withAmplifyCacheValues(Component) {
  return function _withAmplifyCacheValues({ children, ...rest }) {
    const componentName =
      Component.displayName || Component.name || "Component";
    _withAmplifyCacheValues.displayName = `withAmplifyCacheValues(${componentName})`;
    const [darkModeDropdownValue, setDarkModeDropdownValue] = useState(defaultSetting);
    const [systemPrefersDark, setSystemPrefersDark] = useState(
      window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
    );
    const useOverride = [THEME_CACHE_KEYS.light, THEME_CACHE_KEYS.dark].includes(darkModeDropdownValue);
    let componentProps = {
      ...rest
    };
    
    // Function to update dark mode that can be passed to children
    const updateDarkMode = (newValue) => {
      if (newValue !== darkModeDropdownValue) {
        setDarkModeDropdownValue(newValue);
        setStorageItem(THEME_CACHE_KEYS.darkMode, newValue);
      }
    };

    // Check for system preference changes
    useEffect(() => {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      
      const handleChange = (e) => {
        setSystemPrefersDark(e.matches);
      };
      
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
      } 
    }, []);

    // Determine if dark mode should be enabled
    const isDarkMode = useOverride 
      ? darkModeDropdownValue === THEME_CACHE_KEYS.dark 
      : systemPrefersDark; // Use system preference if not overridden
    
    componentProps.darkMode = isDarkMode;
    componentProps.updateDarkMode = updateDarkMode; // Expose the function to update dark mode
    
    // Apply dark mode class to body directly from this component
    useEffect(() => {
      const darkModeClassName = 'dark-mode-enabled';
      const classList = document.body.classList;
      if (isDarkMode) {
        classList.add(darkModeClassName);
      } else if (classList.contains(darkModeClassName)) {
        classList.remove(darkModeClassName);
      }
    }, [isDarkMode]);


    return <Component {...componentProps}>{children}</Component>;
  };
}

export default withAmplifyCacheValues;
